import cx from 'classnames';
import { type Control, useController } from 'react-hook-form';
import { FloorPlan } from '@components/floorPlan/FloorPlan';
import {
  floorPlanTablesRendererFactory,
  interactiveFloorPlanTablesRendererFactory,
} from '@components/floorPlan/floorPlanTablesRendererFactory';
import {
  type FloorPlanData,
  type FloorPlanTableData,
} from '@shared/types/floorPlans';
import typography from '~styles/typography.scss';
import { type ListingFormData } from '../types';
import styles from './FloorPlanFieldset.scss';

const InteractiveFloorPlanForClassicListingEditing = ({
  floorPlan,
  calculateIsHighlighted,
  calculateIsUnused,
  floorPlanSelectionIsInvalid,
  handleFloorPlanTableOnClick,
}: {
  floorPlan: FloorPlanData;
  calculateIsHighlighted: (floorPlanTable: FloorPlanTableData) => boolean;
  calculateIsUnused: (floorPlanTable: FloorPlanTableData) => boolean;
  floorPlanSelectionIsInvalid: boolean;
  handleFloorPlanTableOnClick: (floorPlanTable: FloorPlanTableData) => void;
}) => (
  <FloorPlan
    backgroundSrc={floorPlan?.backgroundSrc}
    floorPlanTablesRenderer={interactiveFloorPlanTablesRendererFactory({
      calculateIsHighlighted,
      calculateIsUnused,
      errorState: floorPlanSelectionIsInvalid,
      handleTableOnClick: handleFloorPlanTableOnClick,
      tables: floorPlan?.floorPlanTables || [],
    })}
  />
);

const StaticFloorPlanForShiftBasedTableEditing = ({
  floorPlan,
  calculateIsHighlighted,
}: {
  floorPlan: FloorPlanData;
  calculateIsHighlighted: (floorPlanTable: FloorPlanTableData) => boolean;
}) => (
  <FloorPlan
    backgroundSrc={floorPlan?.backgroundSrc}
    floorPlanTablesRenderer={floorPlanTablesRendererFactory({
      calculateIsHighlighted,
      dimNonHighlightedTables: true,
      tables: floorPlan?.floorPlanTables || [],
    })}
  />
);

export interface FloorPlanFieldsetProps {
  control: Control<ListingFormData>;
  floorPlan: FloorPlanData;
  hideListingsTerminology: boolean;
}
export const FloorPlanFieldset = ({
  control,
  floorPlan,
  hideListingsTerminology,
}: FloorPlanFieldsetProps) => {
  // hooks
  const {
    field: {
      onChange: handleOnChangeHighlightedTables,
      value: highlightedTables,
    },
    fieldState: { error },
  } = useController({
    control,
    name: 'highlightedTables',
    rules: {
      required: true,
    },
  });

  // handlers
  const handleFloorPlanTableOnClick = (floorPlanTable: FloorPlanTableData) => {
    const indexOfHighlightedFloorPlanTable = highlightedTables.findIndex(
      (highlightedTable) => highlightedTable.id === floorPlanTable.id,
    );
    const isHighlighted = indexOfHighlightedFloorPlanTable !== -1;

    const newHighlightedTables = [...(highlightedTables || [])];
    if (isHighlighted) {
      newHighlightedTables.splice(indexOfHighlightedFloorPlanTable, 1);
    } else {
      newHighlightedTables.push(floorPlanTable);
    }

    handleOnChangeHighlightedTables(newHighlightedTables);
  };

  // derived state
  const calculateIsHighlighted = (floorPlanTable: FloorPlanTableData) =>
    highlightedTables?.some(
      (highlightedTable: FloorPlanTableData) =>
        highlightedTable.id === floorPlanTable.id,
    );

  const calculateIsUnused = (floorPlanTable: FloorPlanTableData) =>
    floorPlanTable.listings.length === 0;

  const floorPlanSelectionIsInvalid = !!error && !highlightedTables.length;

  const floorPlanToRender = hideListingsTerminology ? (
    <StaticFloorPlanForShiftBasedTableEditing
      floorPlan={floorPlan}
      calculateIsHighlighted={calculateIsHighlighted}
    />
  ) : (
    <InteractiveFloorPlanForClassicListingEditing
      floorPlan={floorPlan}
      calculateIsHighlighted={calculateIsHighlighted}
      calculateIsUnused={calculateIsUnused}
      floorPlanSelectionIsInvalid={floorPlanSelectionIsInvalid}
      handleFloorPlanTableOnClick={handleFloorPlanTableOnClick}
    />
  );

  return (
    <fieldset
      className={styles.floorPlanFieldset}
      data-testid="floor-plan-fieldset"
    >
      {!hideListingsTerminology && (
        <legend className={styles.floorPlanLegend}>
          <p
            className={cx({
              [typography.t1]: true,
              [styles.floorPlanInstructions]: true,
              [styles.error]: !!error,
            })}
          >
            Select at least one table to be on the listing
          </p>
          <p className={cx(typography.t1, styles.floorPlanInstructions)}>
            <svg className={styles.tableUnusedSvg}>
              <circle className={styles.tableUnusedIndicator} />
            </svg>
            This table is not used in other listings
          </p>
        </legend>
      )}
      {floorPlanToRender}
      {!hideListingsTerminology && (
        <button
          className={styles.clearSelectionButton}
          onClick={() => handleOnChangeHighlightedTables([])}
          type="button"
        >
          <span role="status">
            {highlightedTables.length}
            {highlightedTables.length === 1 ? ' table ' : ' tables '}
            selected
          </span>
          &nbsp;
          {highlightedTables.length ? (
            <span className={cx(typography.c2, styles.unselectText)}>
              Unselect all
            </span>
          ) : null}
        </button>
      )}
    </fieldset>
  );
};
